import { FC, useMemo } from "react";
import { CreditPayment, CreditType } from "@utilities/types";
import { formatDate, formatPrice } from "@utilities/functions";
import { PaymentMethodeEnum } from "@utilities/enums";
import { Banknote, CreditCard, FileText, Smartphone, Wallet, Calendar, Hash, MessageSquare } from "lucide-react";

interface Props {
  credit: CreditType;
}

const getPaymentIcon = (methode?: PaymentMethodeEnum) => {
  switch (methode) {
    case PaymentMethodeEnum.esp:
      return <Banknote className="w-4 h-4" />;
    case PaymentMethodeEnum.mobilMoney:
      return <Smartphone className="w-4 h-4" />;
    case PaymentMethodeEnum.cb:
      return <CreditCard className="w-4 h-4" />;
    case PaymentMethodeEnum.cheque:
      return <FileText className="w-4 h-4" />;
    default:
      return <Wallet className="w-4 h-4" />;
  }
};

const getPaymentMethodColor = (methode?: PaymentMethodeEnum) => {
  switch (methode) {
    case PaymentMethodeEnum.esp:
      return 'bg-green-100 text-green-800 border-green-200';
    case PaymentMethodeEnum.mobilMoney:
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case PaymentMethodeEnum.cb:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case PaymentMethodeEnum.cheque:
      return 'bg-purple-100 text-purple-800 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const CreditPaymentsHistory: FC<Props> = ({ credit }) => {

  // Calcul du total payé
  const totalPaid = useMemo(() =>
    credit.payments.reduce((sum, payment) => sum + payment.amount, 0),
    [credit.payments]);

  // Trier les paiements par date (du plus récent au plus ancien)
  const sortedPayments = useMemo(() =>
    [...credit.payments].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    ),
    [credit.payments]);

  const renderPaymentDetails = (payment: CreditPayment, index: number) => {
    // Rétrocompatibilité : les anciens paiements n'ont pas de méthode
    const hasMethode = !!payment.methode;

    return (
      <div
        key={index}
        className={`p-4 rounded-xl border ${hasMethode ? getPaymentMethodColor(payment.methode) : 'bg-gray-50 border-gray-200'}`}
      >
        <div className="flex items-center justify-between mb-2">
          {/* Icône et méthode */}
          <div className="flex items-center gap-2">
            {getPaymentIcon(payment.methode)}
            <span className="font-medium text-sm">
              {payment.methode || 'Non spécifié'}
            </span>
            {payment.mobilePayment && (
              <span className="text-xs px-2 py-0.5 bg-white/50 rounded-full">
                {payment.mobilePayment}
              </span>
            )}
          </div>
          {/* Montant */}
          <span className="font-bold text-lg">
            {formatPrice(payment.amount)}
          </span>
        </div>

        {/* Détails supplémentaires */}
        <div className="flex flex-wrap gap-3 text-xs opacity-80">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(payment.date)}
          </div>
          {payment.transactionRef && (
            <div className="flex items-center gap-1">
              <Hash className="w-3 h-3" />
              {payment.transactionRef}
            </div>
          )}
        </div>

        {/* Notes */}
        {payment.notes && (
          <div className="mt-2 pt-2 border-t border-current/10">
            <div className="flex items-start gap-1 text-xs">
              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="italic">{payment.notes}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Résumé */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-green-600 font-medium">Total des paiements</p>
            <p className="text-2xl font-bold text-green-800">{formatPrice(totalPaid)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Reste à payer</p>
            <p className="text-lg font-semibold text-red-600">{formatPrice(credit.remainingToBePaid)}</p>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(totalPaid / credit.amount) * 100}%` }}
            />
          </div>
          <p className="text-xs text-green-600 mt-1 text-right">
            {Math.round((totalPaid / credit.amount) * 100)}% remboursé
          </p>
        </div>
      </div>

      {/* Liste des paiements */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-primary rounded-full"></span>
          Historique ({credit.payments.length} paiement{credit.payments.length > 1 ? 's' : ''})
        </h4>

        {credit.payments.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <Wallet className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Aucun paiement enregistré pour ce crédit.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {sortedPayments.map((payment, index) => renderPaymentDetails(payment, index))}
          </div>
        )}
      </div>
    </div>
  );
};
